import { Button, Dropdown, Tooltip } from 'antd'
import { FC, MouseEventHandler, useCallback, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import type { default as ReactQuillDefault } from 'react-quill'
import { useTranslation } from 'next-i18next'
import type { DeltaStatic } from 'quill'

import { IconClock, IconDictionary, IconTempo } from '@/components/common/Icons'
import { poppins } from '@/components/common/fonts'
import { CustomizedReactQuill } from '@/components/editor/scenes/scriptEditor/CustomizedReactQuill'
import {
  PauseValueForGet,
  PauseValueForSet,
} from '@/components/editor/scenes/scriptEditor/CustomizedReactQuill/modules/pause/types'
import { PAUSE_BLOT_NAME } from '@/components/editor/scenes/scriptEditor/CustomizedReactQuill/modules/pause/constants'
import {
  calcMenuPosition,
  calcMenuPositionByRect,
  Placement,
} from '@/components/editor/scenes/scriptEditor/contextMenus/utils'
import PauseMenu from '@/components/editor/scenes/scriptEditor/contextMenus/PauseMenu'
import { findPauseBlotById } from '@/components/editor/scenes/scriptEditor/CustomizedReactQuill/modules/pause/blot'
import DictionaryPopup from '@/components/editor/scenes/scriptEditor/contextMenus/DictionaryMenu'
import {
  TEXT_SCRIPT_TEMPOS,
  TextScriptTempo,
} from '@/constants/tools/solid-background-video'

import { useToolsNotification } from '../../../common/hooks/notification'

const modules = {
  toolbar: false,
  clipboard: {
    matchVisual: false,
  },
}

export const TextScript: FC<{
  textScript?: DeltaStatic
  onChangeTextScript: ({}: {
    html: string
    delta: DeltaStatic
    plainText: string
  }) => void
  onPasteTextScript: () => void

  tempo: TextScriptTempo
  onChangeTempo: (tempo: TextScriptTempo) => void

  dictionary: Record<string, string>
  onChangeDictionary: (dictionary: Record<string, string>) => void

  onGenerate: () => void
  disabledGenerate: boolean
  loadingGenerate: boolean
}> = ({
  textScript,
  onChangeTextScript,
  onPasteTextScript,

  tempo,
  onChangeTempo,
  dictionary,
  onChangeDictionary,

  onGenerate,
  disabledGenerate,
  loadingGenerate,
}) => {
  const { t } = useTranslation('common')

  const { open: openNotification, contextHolder } = useToolsNotification()

  const editorRef = useRef<ReactQuillDefault>(null)

  /**
   * 일시 정지 관련 상태
   */
  const [openedPauseInfo, setOpenedPauseInfo] = useState<{
    id: string
    duration: number
    position: {
      placement: Placement
      dimension: {
        top: number | string
        left: number | string
        right: number | string
        bottom: number | string
      }
    }
  }>()

  /**
   * 사전 관련 상태
   */
  const [openedDictionaryInfo, setOpenedDictionaryInfo] = useState<{
    selectedWord: string
    dictionaryWord: string
    placement: Placement
    dimension: {
      top: number | string
      left: number | string
      right: number | string
      bottom: number | string
    }
  }>()

  /**
   * 팝업 모두 닫기
   */
  const closeAllActionModals = useCallback(() => {
    setOpenedPauseInfo(undefined)
    setOpenedDictionaryInfo(undefined)
  }, [])

  /**
   * 일시 정지 삽입
   */
  const handleAddPause = () => {
    if (!editorRef.current) {
      openNotification(t('Cursor must be placed on the script'))
      return
    }

    const editor = editorRef.current.getEditor()
    const selection = editor.getSelection()

    if (!selection) {
      openNotification(t('Cursor must be placed on the script'))
      return
    }

    const pauseValue: PauseValueForSet = {
      duration: 0.5,
    }

    const cursorPosition = selection.index

    editor.insertEmbed(cursorPosition, PAUSE_BLOT_NAME, pauseValue)
    editor.insertText(cursorPosition + 1, ' ')
    editor.setSelection(cursorPosition + 2, 0)
  }

  /**
   * 일시 정지 입력 모달 열기
   */
  const handleOpenPauseModal = useCallback(
    (target: HTMLElement, value: PauseValueForGet) => {
      // 기존 모달 닫기
      closeAllActionModals()

      /**
       * 이미 열려있으면 다시 열지 않음
       */
      if (openedPauseInfo?.id === value.id) {
        return
      }

      const position = calcMenuPosition(target)

      setOpenedPauseInfo({
        id: value.id,
        duration: value.duration,
        position,
      })
    },
    [openedPauseInfo, closeAllActionModals],
  )

  /**
   * 퍼즈 입력 모달 닫기
   */
  const handleClosePauseModal = () => {
    setOpenedPauseInfo(undefined)
  }

  /**
   * 퍼즈 입력 저장
   */
  const handleSavePauseModal = useCallback(
    (duration: number) => {
      if (!openedPauseInfo) {
        return
      }

      const { id } = openedPauseInfo

      const foundBlot = findPauseBlotById(id)

      if (!foundBlot) {
        return
      }

      foundBlot.replaceWith('pause', {
        duration,
      })
    },
    [openedPauseInfo],
  )

  /**
   * 사전 입력 모달 열기\
   * 액션 바의 사전 버튼 클릭했을 때 사용
   */
  const handleAddDictionaryWord: MouseEventHandler = (e) => {
    /**
     * 이벤트 전파를 막아 팝업이 바로 닫히는 현상 방지
     */
    e.stopPropagation()

    const editor = editorRef.current?.getEditor()

    if (!editor) {
      openNotification(t('Please select a word'))

      return
    }

    const selection = editor.getSelection()

    /**
     * 셀렉션이 없으면 중단
     */
    if (!selection) {
      openNotification(t('Please select a word'))

      return
    }

    closeAllActionModals()

    /**
     * 1. 현재 셀렉션 근처에 이미 등록된 사전 단어가 있는지 확인
     * 2. 있으면 해당 단어를 선택 후 진행
     * 3. 없으면 셀렉션 길이, 공백/개행 포함 여부 확인 후 진행
     */

    /**
     * 에디터 내용을 텍스트로 변환\
     * 태그(일시정지, 제스쳐) 등은 1글자로 취급됨\
     * `insert`가 `string`이 아닌 경우에는 태그임
     */
    const text = editor
      .getContents()
      .map((op) => (typeof op.insert === 'string' ? op.insert : ' '))
      .join('')

    /**
     * 사전에 등록된 단어들의 에디터 내의 위치
     */
    type DictionaryWordPosition = {
      word: string
      position: {
        index: number
        length: number
      }
    }

    /**
     * 에디터 내에 실제로 강조(밑줄) 표시되는 단어들의 위치
     *
     * 사전에 등록된 단어들 중에서 에디터에 표시되거나, 표시되지 않는 이유:\
     * 강조(밑줄)를 모든 단어에 표시하는 경우 중첩하여 표시하도록 구현해야 하는데\
     * 현재 구현 방식 특성 상 성능 문제와 UI 상 모호함이 발생할 수 있음\
     * 그래서 강조 표시가 중첩되어 표시되지 않도록 처리되어 있음\
     * ex:\
     *   문장: 가나다라마바사\
     *   사전 등록 단어: 나다라, 다라마\
     *   강조 표시되는 단어: 나다라
     */
    let positionsOfHighlightedDictionaryWords: DictionaryWordPosition[] = []

    let copiedText = text

    for (const word of Object.keys(dictionary)) {
      let index = copiedText.indexOf(word)

      while (index !== -1) {
        positionsOfHighlightedDictionaryWords.push({
          word,
          position: { index, length: word.length },
        })
        index = copiedText.indexOf(word, index + word.length)
        /**
         * 검색된 단어는 들어내어 다른 단어와 중첩되지 않도록 처리
         */
        copiedText = copiedText.replace(word, ' '.repeat(word.length))
      }
    }

    /**
     * 위치 순으로 정렬하여 눈에 보이는 단어 중 앞에 있는 것부터 선택되도록 함
     */
    positionsOfHighlightedDictionaryWords =
      positionsOfHighlightedDictionaryWords.sort(
        (a, b) => a.position.index - b.position.index,
      )

    let foundWord = ''
    let foundIndex = -1

    /**
     * 1. 정확히 일치하는 단어 확인
     */
    const foundExactWord = positionsOfHighlightedDictionaryWords.find(
      ({ position }) =>
        selection.index === position.index &&
        selection.length === position.length,
    )

    if (!!foundExactWord) {
      foundWord = foundExactWord.word
      foundIndex = foundExactWord.position.index
    }

    /**
     * 2. 선택된 영역에 포함되는 단어 확인
     */
    if (!foundWord) {
      const foundIncludedWord = positionsOfHighlightedDictionaryWords.find(
        ({ position }) =>
          selection.index < position.index + position.length &&
          selection.index + selection.length > position.index,
      )

      if (!!foundIncludedWord) {
        foundWord = foundIncludedWord.word
        foundIndex = foundIncludedWord.position.index
      }
    }

    /**
     * 3. 커서에 인접한 단어 확인
     */
    if (!foundWord && selection.length === 0) {
      const foundNearWord = positionsOfHighlightedDictionaryWords.find(
        ({ position }) =>
          selection.index === position.index ||
          selection.index === position.index + position.length,
      )

      if (!!foundNearWord) {
        foundWord = foundNearWord.word
        foundIndex = foundNearWord.position.index
      }
    }

    let selectedWord

    /**
     * 찾은 단어가 있으면 해당 단어로 선택 보정
     */
    if (!!foundWord) {
      editor.setSelection(foundIndex, foundWord.length)
      selectedWord = foundWord
    } else {
      /**
       * 찾은 단어가 없는 경우 사용자가 선택한 영역을 그대로 사용
       */
      /**
       * 선택된 길이가 0이면 중단
       */
      if (selection.length === 0) {
        openNotification(t('Please select a word'))

        return
      }

      selectedWord = editor.getText(selection.index, selection.length)

      /**
       * 공백이나 개행 등이 포함되어 있으면 중단
       */
      if (/\s/.test(selectedWord)) {
        openNotification(t('Please select a word'))

        return
      }
    }

    const range = window.getSelection()?.getRangeAt(0)

    if (!range) {
      alert(t('please select a word'))

      return
    }

    const rect = range.getBoundingClientRect()
    const position = calcMenuPositionByRect(rect)

    const dictionaryWord = dictionary[selectedWord] ?? ''

    setOpenedDictionaryInfo({
      selectedWord,
      dictionaryWord,
      placement: position.placement,
      dimension: position.dimension,
    })
  }

  const handleCloseDictionaryModal = () => {
    setOpenedDictionaryInfo(undefined)
  }

  /**
   * 사전 입력 모달 열기\
   * 에디터 내에서 강조된 단어를 클릭했을 때 사용
   */
  const handleClickRegisteredDictionaryWord = (
    e: MouseEvent,
    word: string,
    rect: DOMRect,
  ) => {
    /**
     * 이벤트 전파를 막아 팝업이 바로 닫히는 현상 방지
     */
    e.stopPropagation()

    const dictionaryWord = dictionary[word] ?? ''

    if (!dictionaryWord) {
      return
    }

    const position = calcMenuPositionByRect(rect)

    setOpenedDictionaryInfo({
      selectedWord: word,
      dictionaryWord,
      placement: position.placement,
      dimension: position.dimension,
    })
  }

  return (
    <TextScriptContainer>
      <EditorContainer onPaste={onPasteTextScript}>
        <CustomizedReactQuill
          modules={modules}
          ref={editorRef}
          value={textScript}
          onChange={(value, _, __, editor) =>
            onChangeTextScript({
              html: value,
              delta: editor.getContents(),
              plainText: editor.getText(),
            })
          }
          formats={['underline']}
          pause={{
            onClick: handleOpenPauseModal,
          }}
          dictionaryHighlight={{
            getDictionary: () => dictionary,
            getSelectedWord: () => openedDictionaryInfo?.selectedWord,
            onClickRegisteredWord: handleClickRegisteredDictionaryWord,
          }}
        />
      </EditorContainer>

      <Footer>
        <Actions>
          {/* 
            템포 기능은 더이상 제공하지 않음
          */}
          {/* <Dropdown
            menu={{
              items: TEXT_SCRIPT_TEMPOS.map((_tempo) => ({
                key: _tempo,
                label: _tempo,
                onClick: () => onChangeTempo(_tempo),
              })),
            }}
            trigger={['click']}
          >
            <Tooltip title={t('Scene Speed')}>
              <ActionButton type="text">
                {tempo === '1.0' ? (
                  <IconTempo
                    width={16}
                    height={16}
                    css={css`
                      path {
                        stroke: currentColor;
                      }
                    `}
                  />
                ) : (
                  `${tempo}x`
                )}
              </ActionButton>
            </Tooltip>
          </Dropdown> */}

          <Tooltip title={t('Add Pause')}>
            <ActionButton type="text" onClick={handleAddPause}>
              <IconClock
                width={16}
                height={16}
                css={css`
                  path {
                    stroke: currentColor;
                  }
                `}
              />
            </ActionButton>
          </Tooltip>

          <Tooltip title={t('Dictionary')}>
            <ActionButton type="text" onClick={handleAddDictionaryWord}>
              <IconDictionary
                width={16}
                height={16}
                css={css`
                  path: {
                    fill: currentColor;
                  }
                `}
              />
            </ActionButton>
          </Tooltip>
        </Actions>

        <GenerateButton
          onClick={onGenerate}
          disabled={disabledGenerate}
          loading={loadingGenerate}
          type="primary"
        >
          {loadingGenerate ? t('Processing') : t('Generate')}
        </GenerateButton>
      </Footer>

      <div
        css={{
          position: 'fixed',
        }}
      >
        {/* 
          일시 정지 입력 모달
        */}
        {!!openedPauseInfo && (
          <PauseMenu
            position={openedPauseInfo.position}
            duration={openedPauseInfo.duration}
            onSave={handleSavePauseModal}
            onClose={handleClosePauseModal}
          />
        )}

        {/* 
          사전 등록 모달
        */}
        {!!openedDictionaryInfo && (
          <DictionaryPopup
            position={openedDictionaryInfo}
            onSave={(selectedWord: string, dictionaryWord: string) => {
              onChangeDictionary({
                ...dictionary,
                [selectedWord]: dictionaryWord,
              })
            }}
            onClear={(selectedWord: string) => {
              const cloned = { ...dictionary }
              delete cloned[selectedWord]
              onChangeDictionary(cloned)
            }}
            onClose={handleCloseDictionaryModal}
            selectedWord={openedDictionaryInfo.selectedWord}
            dictionaryWord={openedDictionaryInfo.dictionaryWord}
          />
        )}
      </div>

      {contextHolder}
    </TextScriptContainer>
  )
}

const TextScriptContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  height: 100%;
`

const EditorContainer = styled.div`
  flex: 1;

  overflow: hidden;

  border: 1px solid #cccccc;
  border-top: 0;
  border-radius: 0 0 6px 6px;

  padding: 8px;

  .quill {
    height: 100%;
  }

  .ql-container {
    border: none;

    font-size: 16px;
    line-height: 28px;
    position: relative;
  }

  .ql-editor {
    width: 100%;
    overflow-y: visible;
    position: relative;
  }
`

const Footer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  justify-content: space-between;

  height: 70px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const ActionButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 40px;
  height: 40px;

  border: 0;
  box-shadow: none;

  background-color: #f4f4f4;
`

const GenerateButton = styled(Button)`
  height: 40px;

  font-family: ${poppins.string};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  background-color: #146ef5;

  border-radius: 8px;
  padding-inline: 24px;
`
